import React from 'react';
import { Bed, Maximize, Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { apartmentUnits } from '../data/mock';

const ApartmentsSection = () => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="apartments" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-amber-600 font-semibold tracking-wider uppercase text-sm">
            Our Collection
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-slate-900 font-medium mt-4 mb-6">
            Luxury Apartments
          </h2>
          <p className="text-slate-500 text-lg">
            Choose from our carefully designed apartments, each offering 
            uncompromising quality in Kampala&apos;s most coveted location.
          </p>
        </div>

        {/* Apartments Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {apartmentUnits.map((unit, index) => (
            <div
              key={unit.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-slate-100"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={unit.image}
                  alt={unit.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
                
                {/* Badge */}
                {unit.name === 'Penthouse' && (
                  <div className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    Premium
                  </div>
                )}

                {/* Quick Stats */}
                <div className="absolute bottom-4 left-4 flex gap-3">
                  <div className="flex items-center gap-1.5 bg-white/95 rounded-full px-3 py-1.5">
                    <Bed className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">{unit.bedrooms} Bed</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/95 rounded-full px-3 py-1.5">
                    <Maximize className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">{unit.size} m²</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Title & Price */}
                <div className="flex justify-between items-start mb-5">
                  <div>
                    <h3 className="font-serif text-xl font-medium text-slate-900">
                      {unit.name}
                    </h3>
                    <p className="text-slate-400 text-sm mt-1">Nakasero Hill</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-2xl font-medium text-amber-600">
                      {formatPrice(unit.price)}
                    </p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2.5 mb-6">
                  {unit.features.slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <Button
                  onClick={scrollToContact}
                  className="w-full bg-slate-900 hover:bg-amber-600 text-white rounded-xl py-5 transition-all duration-300"
                >
                  <span>Inquire Now</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ApartmentsSection;
